import { useState, useEffect } from "react";
import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import { generateAdvisorHighlights, updateAdvisor } from "../services/gutcheck-admin-api.service";
import { useAuth0 } from "@auth0/auth0-react";
import { IAdvisor, IProject } from "../../../types";

export const HighlightsTab = ({ 
    advisor, 
    onUpdate, 
    onFieldUpdate 
}: { 
    advisor: IAdvisor, 
    onUpdate: () => void,
    onFieldUpdate?: (updates: Partial<IAdvisor>) => void
}) => {
    const { getAccessTokenSilently } = useAuth0();
    const [generatingHighlights, setGeneratingHighlights] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const initialHighlights = advisor.highlights_json ? JSON.parse(advisor.highlights_json).highlights : ['', '', ''];
    const [highlights, setHighlights] = useState<string[]>(initialHighlights);

    // Auto-clear success message after 3 seconds
    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => {
                setSuccessMessage(null);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [successMessage]);

    // Update highlights when advisor prop changes
    useEffect(() => {
        const newHighlights = advisor.highlights_json ? JSON.parse(advisor.highlights_json).highlights : ['', '', ''];
        setHighlights(newHighlights);
    }, [advisor.highlights_json]);

    const handleHighlightChange = (index: number, value: string) => {
        const newHighlights = [...highlights];
        newHighlights[index] = value;
        setHighlights(newHighlights);
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setIsSubmitting(true);
        setSuccessMessage(null);
        setErrorMessage(null);

        if (!advisor.survey_link?.project?.id) {
            setErrorMessage("Project ID is required");
            setIsSubmitting(false);
            return;
        }

        try {
            const accessToken = await getAccessTokenSilently();
            const newAdvisor = { 
                highlights_json: JSON.stringify({ highlights: highlights })
            };
            
            await updateAdvisor(advisor.survey_link.project.id, advisor.id, newAdvisor, accessToken);
            setSuccessMessage("Highlights updated successfully");
            
            // Update the local advisor state to reflect the saved changes
            if (onFieldUpdate) {
                onFieldUpdate({
                    highlights_json: JSON.stringify({ highlights: highlights })
                });
            }
        } catch (error) {
            setErrorMessage("An error occurred while updating highlights");
            console.error(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleGenerateHighlights = async () => {
        setGeneratingHighlights(true);
        setSuccessMessage(null);
        setErrorMessage(null);

        if (!advisor.survey_link?.project?.id) {
            setErrorMessage("Project ID is required");
            setGeneratingHighlights(false);
            return;
        }

        try {
            const accessToken = await getAccessTokenSilently();
            const { data, error } = await generateAdvisorHighlights(advisor.survey_link.project.id, advisor.uuid, accessToken);
            
            if (error) {
                setErrorMessage("Failed to generate highlights");
                console.error(error);
            } else {
                const updatedAdvisor = data.advisor;
                const generatedHighlights = JSON.parse(updatedAdvisor.highlights_json).highlights;
                setHighlights(generatedHighlights);
                setSuccessMessage("Highlights generated successfully");
                
                // Update the local advisor state to reflect the generated changes
                if (onFieldUpdate) {
                    onFieldUpdate({
                        highlights_json: updatedAdvisor.highlights_json
                    });
                }
            }
        } catch (error) {
            setErrorMessage("An error occurred while generating highlights");
            console.error(error);
        } finally {
            setGeneratingHighlights(false);
        }
    };

    return (
        <Box sx={{ p: 3 }}>            
            <Box component="form" onSubmit={handleSubmit}>
                <Stack spacing={2} sx={{ marginTop: '20px' }}>
                    <TextField
                        label="Highlight 1"
                        value={highlights[0]}
                        onChange={(e) => handleHighlightChange(0, e.target.value)}
                        multiline
                        rows={4}
                        fullWidth
                        size="small"
                    />
                    <TextField
                        label="Highlight 2"
                        value={highlights[1]}
                        onChange={(e) => handleHighlightChange(1, e.target.value)}
                        multiline
                        rows={4}
                        fullWidth
                        size="small"
                    />
                    <TextField
                        label="Highlight 3"
                        value={highlights[2]}
                        onChange={(e) => handleHighlightChange(2, e.target.value)}
                        multiline
                        rows={4}
                        fullWidth
                        size="small"
                    />

                    <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Button 
                            variant="outlined"
                            onClick={handleGenerateHighlights} 
                            disabled={generatingHighlights}
                        >
                            Generate Highlights
                        </Button>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            {successMessage && <Typography variant="body1" color="success.main">{successMessage}</Typography>}
                            {errorMessage && <Typography variant="body1" color="error.main">{errorMessage}</Typography>}
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                disabled={isSubmitting}
                            >
                                Save Highlights
                            </Button>
                        </Box>
                    </Box>
                </Stack>
            </Box>
        </Box>
    );
}; 